import { useEffect, useState } from "react";
import "../Common/css/styles.css";
import "../Common/css/user.css";
import { createAdmin, deleteAdmin, getAdminDetails, resetAdminPassword, updateAdmin } from "src/Services/user_api";
import { Button, Dialog, FormControl, FormControlLabel, FormLabel, Grid, IconButton, Menu, MenuItem, Radio, RadioGroup, Select, TextField } from "@mui/material";
import { Close, MoreVert } from "@mui/icons-material";
import { PlusOutlined } from "@ant-design/icons";
import TableToolbar from "src/Components/TableToolbar";
import CustomAlert from "src/Components/Alerts";
import Spinner from "src/Components/Spinner";
import Dialogbox from "src/Components/Dialogbox";

const ITEMS_PER_PAGE = 5;

export default function AdminMaintenance() {
  const token = sessionStorage.getItem("token") || "";
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertDescription, setAlertDescription] = useState("");
  const [alertType, setAlertType] = useState<"success" | "error" | "warning">("success");
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isOpen2, setIsOpen2] = useState(false);
  const [admins, setAdmins] = useState<any[]>([]);
  const [filteredAdmins, setFilteredAdmins] = useState<any[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAdmin, setSelectedAdmin] = useState<any>();
  const [openModal, setOpenModal] = useState(false);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [emergencyContactNumber, setEmergencyContactNumber] = useState("");
  const [nic, setNic] = useState("");
  const [gender, setGender] = useState("");
  const [adminType, setAdminType] = useState(1);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, admin: any) => {
    setAnchorEl(event.currentTarget);
    //console.log(`Selected admin: ${admin.id}`);

    setSelectedAdmin(admin);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const showAlert = (description: string, type: "success" | "error" | "warning") => {
    setAlertDescription(description);
    setAlertType(type);
    setAlertOpen(true);
  };

  const handleView = () => {
    if (!selectedAdmin) return;
    setViewDialogOpen(true);
  };

  const handleEdit = () => {
    if (!selectedAdmin) return;
    setName(selectedAdmin.name);
    setEmail(selectedAdmin.email);
    setContactNumber(selectedAdmin.contactNumber);
    setEmergencyContactNumber(selectedAdmin.emergencyContactNumber);
    setNic(selectedAdmin.nic);
    setAddress(selectedAdmin.address);
    setGender(selectedAdmin.gender);
    setAdminType(selectedAdmin.adminType);
    setIsViewMode(true);
    setOpenModal(true);
  };

  const handleDelete = async (id: any) => {
    try {
      await deleteAdmin(token, id);
      showAlert("Admin deleted successfully", "success");
      handleFetchAdmins();
    } catch (error: any) {
      showAlert(error.response.data.message, "error");
    }
    setIsOpen(false);
  };

  useEffect(() => {
    handleFetchAdmins();
  }, []);

  useEffect(() => {
    handleSearch(searchTerm);
  }, [admins, searchTerm]);

  const handleFetchAdmins = async () => {
    try {
      const res = await getAdminDetails(token);
      setAdmins(res);
    } catch (error) {
      console.error("Error fetching admins:", error);
    }
  };

  const handleSearch = (term: string) => {
    const lower = term.toLowerCase();
    const filtered = admins.filter(
      (admin) =>
        admin.name.toLowerCase().includes(lower) ||
        admin.email.toLowerCase().includes(lower)
    );
    setFilteredAdmins(filtered);
    setCurrentPage(1);
  };

  const paginatedAdmins = filteredAdmins.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const totalPages = Math.ceil(filteredAdmins.length / ITEMS_PER_PAGE);

  const validateForm = () => {
    if (!name || !email || !contactNumber || !password || !nic || !address || !emergencyContactNumber || !gender) {
      showAlert("Please fill in all fields", "error");
      return false;
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      showAlert("Please enter a valid email address", "error");
      return false;
    }
    const contactPattern = /^\d{10}$/;
    if (!contactPattern.test(contactNumber)) {
      showAlert("Please enter a valid contact number", "error");
      return false;
    }
    const emergencyContactPattern = /^\d{10}$/;
    if (!emergencyContactPattern.test(emergencyContactNumber)) {
      showAlert("Please enter a valid emergency contact number", "error");
      return false;
    }
    const nicPattern = /^[0-9]{9}[vV]$|^[0-9]{12}$/;
    if (!nicPattern.test(nic)) {
      showAlert("Please enter a valid NIC", "error");
      return false;
    }
    return true;
  }

  const validateUpdateForm = () => {
    if (!name || !email || !contactNumber || !nic || !address || !emergencyContactNumber || !gender) {
      showAlert("Please fill in all fields", "error");
      return false;
    }
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      showAlert("Please enter a valid email address", "error");
      return false;
    }
    const contactPattern = /^\d{9}$/;
    if (!contactPattern.test(contactNumber)) {
      showAlert("Please enter a valid contact number", "error");
      return false;
    }
    const emergencyContactPattern = /^\d{9}$/;
    if (!emergencyContactPattern.test(emergencyContactNumber)) {
      showAlert("Please enter a valid emergency contact number", "error");
      return false;
    }
    const nicPattern = /^[0-9]{9}[vV]$|^[0-9]{12}$/;
    if (!nicPattern.test(nic)) {
      showAlert("Please enter a valid NIC", "error");
      return false;
    }
    return true;
  }

  const handleAddAdmin = async () => {
    setIsLoading(true);
    if (!validateForm()) {
      setIsLoading(false);
      return;
    }
    const newAdmin = {
      name,
      password,
      email,
      address,
      contactNumber,
      emergencyContactNumber,
      nic,
      gender,
      adminType
    }
    try {
      await createAdmin(token, newAdmin);
      setTimeout(() => {
        setIsLoading(false);
        handleResetForm();
        handleFetchAdmins();
        setOpenModal(false);
        showAlert("Admin created successfully", "success");
      }, 3000);
    } catch (error: any) {
      setIsLoading(false);
      showAlert(error.response.data.message, "error");
    }
  };

  const handleUpdateAdmin = async () => {
    setIsLoading(true);
    if (!validateUpdateForm()) {
      setIsLoading(false);
      return;
    }
    const updatedAdmin = {
      id: selectedAdmin.id,
      name,
      email,
      contactNumber,
      emergencyContactNumber,
      nic,
      address,
      gender,
      adminType
    };

    try {
      await updateAdmin(token, updatedAdmin);
      setTimeout(() => {
        setIsLoading(false);
        handleResetForm();
        handleFetchAdmins();
        setOpenModal(false);
        showAlert("Admin updated successfully", "success");
      }, 3000);
    } catch (error: any) {
      setIsLoading(false);
      showAlert(error.response.data.message, "error");
    }
  };

  const handleResetPassword = async () => {
    setIsLoading(true);
    try {
      await resetAdminPassword(token, selectedAdmin.email, selectedAdmin.email.split("@")[0]);
      setTimeout(() => {
        setIsLoading(false);
        handleResetForm();
        handleFetchAdmins();
        setOpenModal(false);
        showAlert("Password reset successfully. Your new password is: " + selectedAdmin.email.split("@")[0], "success");
      }, 3000);
    } catch (error: any) {
      setIsLoading(false);
      showAlert(error.response.data.message, "error");
    }
  }

  const handleResetForm = () => {
    setName("");
    setPassword("");
    setEmail("");
    setAddress("");
    setContactNumber("");
    setEmergencyContactNumber("");
    setNic("");
    setGender("");
    setAdminType(1);
  }

  return (
    <div className="comman-outer">
      <Dialogbox
        open={isOpen}
        title="Delete Admin"
        content="Are you sure you want to delete this admin?"
        agreeButtonText="Delete"
        disagreeButtonText="Cancel"
        onAgree={() => handleDelete(selectedAdmin.id)}
        onDisagree={() => setIsOpen(false)}
        onClose={() => setIsOpen(false)}
      />
      <Dialogbox
        open={isOpen2}
        title="Reset Password"
        content="Are you sure you want to reset the password for this admin?"
        agreeButtonText="Reset"
        disagreeButtonText="Cancel"
        onAgree={() => handleResetPassword()}
        onDisagree={() => setIsOpen2(false)}
        onClose={() => setIsOpen2(false)}
      />
      <CustomAlert
        open={alertOpen}
        onClose={() => setAlertOpen(false)}
        description={alertDescription}
        type={alertType}
        timeout={3000}
      />
      <Spinner isLoading={isLoading} />
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { backgroundColor: "#c6c2c2e3", color: "white", paddingBottom: 2, borderRadius: 2, boxShadow: 24, border: "1px solid #ffffffff" },
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", borderBottom: "1px solid #555", backgroundColor: "#393939", padding: "25px 16px" }}>
          <h3>Admin Details</h3>
        </div>

        <Grid container spacing={2} style={{ padding: "25px 16px", color: "white", backgroundColor: "#6c6b6bff"  }} className="form-grid">
          <Grid item xs={12} sm={6}>
            <strong>Name:</strong> {selectedAdmin?.name}
          </Grid>
          <Grid item xs={12} sm={6}>
            <strong>Email:</strong> {selectedAdmin?.email}
          </Grid>
          <Grid item xs={12} sm={6}>
            <strong>Contact:</strong> {selectedAdmin?.contactNumber}
          </Grid>
          <Grid item xs={12} sm={6}>
            <strong>Contact 2:</strong> {selectedAdmin?.emergencyContactNumber}
          </Grid>
          <Grid item xs={12} sm={6}>
            <strong>NIC:</strong> {selectedAdmin?.nic}
          </Grid>
          <Grid item xs={12} sm={6}>
            <strong>Gender:</strong> {selectedAdmin?.gender}
          </Grid>
          <Grid item xs={12}>
            <strong>Address:</strong> {selectedAdmin?.address}
          </Grid>
        </Grid>
      </Dialog>

      {!openModal && (
        <div>
          <TableToolbar
            title="Admin List"
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonText="Admin"
            onButtonClick={() => setOpenModal(true)}
            buttonIcon={<PlusOutlined style={{ fontSize: "14px", color: "black" }} />}
          />
          <div className="space-bar"></div>

          {paginatedAdmins.length > 0 ? paginatedAdmins.map((admin) => (
            <div className="card-outer" key={admin.id}>
              <div className="card-outer1">
                <p className="t-card">{admin.name}</p>
                <p className="t-card">{admin.contactNumber}</p>
                <p className="t-card">{admin.email}</p>
                <p className="t-card">{admin.gender}</p>
              </div>

              <IconButton onClick={(e) => handleMenuClick(e, admin)}>
                <MoreVert style={{ color: "white" }} />
              </IconButton>
            </div>
          )) : (
            <div style={{ justifyContent: "center", alignItems: "center" }} className="card-outer">No admins found.</div>
          )}

          <div className="pagination-footer">
            <span style={{ fontSize: "13px" }}>
              Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} -{" "}
              {Math.min(currentPage * ITEMS_PER_PAGE, filteredAdmins.length)} of{" "}
              {filteredAdmins.length}
            </span>
            <div className="pagination-buttons">
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((prev) => prev - 1)}
              >
                Prev
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentPage(i + 1)}
                  className={currentPage === i + 1 ? "active" : ""}
                >
                  {i + 1}
                </button>
              ))}
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                Next
              </button>
            </div>
          </div>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem onClick={() => {
              handleView();
              handleMenuClose();
            }}>View</MenuItem>
            <MenuItem onClick={() => {
              handleEdit();
              handleMenuClose();
            }}>Edit</MenuItem>
            <MenuItem onClick={() => {
              setIsOpen(true);
              //console.log(`Selected admin for deletion: ${selectedAdmin ? selectedAdmin.id : "None"}`);
              setSelectedAdmin(selectedAdmin);
              handleMenuClose();
            }}>Delete</MenuItem>
            <MenuItem onClick={() => {
              setIsOpen2(true);
              setSelectedAdmin(selectedAdmin);
              handleMenuClose();
            }}>Reset Password</MenuItem>
          </Menu>
        </div>
      )}

      {openModal && (
        <div>
          <div className="table-title-outer">
            {isViewMode ? (
              <div className="table-title">Edit Admin</div>
            ) : (
              <div className="table-title">Add New Admin</div>
            )}
            <IconButton
              className="close-btn"
              onClick={() => {
                setOpenModal(false)
                window.location.reload();
                setIsViewMode(false);
              }}
              style={{ color: "white" }}
            >
              <Close style={{ color: "white" }} />
            </IconButton>
          </div>
          <div className="space-bar"></div>
          <div className="card-outer">
            <Grid container spacing={2} style={{ padding: "16px" }} className="form-grid ">
              <Grid item xs={12} sm={6} md={isViewMode ? 6 : 4}>
                {/* add label */}
                <FormLabel component="legend" sx={{ color: "#3d3d3dff", mb: 1 }}>Name</FormLabel>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Name"
                  size="small"
                  value={name}
                  onChange={(e) => {
                    const onlyText = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                    setName(onlyText);
                  }}
                  sx={{
                    backgroundColor: "black",
                    input: {
                      color: "#ffffff",
                      "::placeholder": {
                        color: "grey",
                        opacity: 1,
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#555",
                      },
                      "&:hover fieldset": {
                        borderColor: "#aaa",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "gray",
                      },
                    },
                  }}
                />
              </Grid>
              {!isViewMode && (
                <Grid item xs={12} sm={6} md={4}>
                  <FormLabel component="legend" sx={{ color: "#3d3d3dff", mb: 1 }}>Password</FormLabel>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Password"
                    type="text"
                    size="small"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    sx={{
                      backgroundColor: "black",
                      input: {
                        color: "#ffffff",
                        "::placeholder": {
                          color: "grey",
                          opacity: 1,
                        },
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "#555",
                        },
                        "&:hover fieldset": {
                          borderColor: "#aaa",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "gray",
                        },
                      },
                    }}
                  />
                </Grid>
              )}

              {!isViewMode && (
                <Grid item xs={12} sm={6} md={4}>
                  <FormLabel component="legend" sx={{ color: "#3d3d3dff", mb: 1 }}>Email</FormLabel>
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder="Email"
                    size="small"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    sx={{
                      backgroundColor: "black",
                      input: {
                        color: "#ffffff",
                        "::placeholder": {
                          color: "grey",
                          opacity: 1,
                        },
                      },
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          borderColor: "#555",
                        },
                        "&:hover fieldset": {
                          borderColor: "#aaa",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "gray",
                        },
                      },
                    }}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} md={isViewMode ? 6 : 4}>
                <FormLabel component="legend" sx={{ color: "#3d3d3dff", mb: 1 }}>Contact Number</FormLabel>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Contact Number"
                  size="small"
                  value={contactNumber}
                  onChange={(e) => setContactNumber(e.target.value)}
                  sx={{
                    backgroundColor: "black",
                    input: {
                      color: "#ffffff",
                      "::placeholder": {
                        color: "grey",
                        opacity: 1,
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#555",
                      },
                      "&:hover fieldset": {
                        borderColor: "#aaa",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "gray",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={isViewMode ? 6 : 4}>
                <FormLabel component="legend" sx={{ color: "#3d3d3dff", mb: 1 }}>Contact Number 2</FormLabel>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Contact Number 2"
                  size="small"
                  value={emergencyContactNumber}
                  onChange={(e) => setEmergencyContactNumber(e.target.value)}
                  sx={{
                    backgroundColor: "black",
                    input: {
                      color: "#ffffff",
                      "::placeholder": {
                        color: "grey",
                        opacity: 1,
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#555",
                      },
                      "&:hover fieldset": {
                        borderColor: "#aaa",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "gray",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={isViewMode ? 6 : 4}>
                <FormLabel component="legend" sx={{ color: "#3d3d3dff", mb: 1 }}>NIC</FormLabel>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="NIC"
                  size="small"
                  value={nic}
                  onChange={(e) => setNic(e.target.value)}
                  sx={{
                    backgroundColor: "black",
                    input: {
                      color: "#ffffff",
                      "::placeholder": {
                        color: "grey",
                        opacity: 1,
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#555",
                      },
                      "&:hover fieldset": {
                        borderColor: "#aaa",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "gray",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <FormLabel component="legend" sx={{ color: "#3d3d3dff", mb: 1 }}>Address</FormLabel>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Address"
                  size="small"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  sx={{
                    backgroundColor: "black",
                    input: {
                      color: "#ffffff",
                      "::placeholder": {
                        color: "grey !important",
                        opacity: 1,
                      },
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#555",
                      },
                      "&:hover fieldset": {
                        borderColor: "#aaa",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: "gray",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={12}>
                <FormControl component="fieldset">
                  <FormLabel
                    component="legend"
                    sx={{
                      color: "#3d3d3dff",
                      "&.Mui-focused": {
                        color: "#3d3d3dff", // Prevent blue on focus
                      },
                      "&.MuiFormLabel-root": {
                        color: "#3d3d3dff",
                      },
                    }}
                  >
                    Gender
                  </FormLabel>

                  <RadioGroup
                    row
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                  >
                    <FormControlLabel
                      value="Male"
                      control={
                        <Radio
                          sx={{
                            color: "#8f8d8dff", // default color
                            "&.Mui-checked": {
                              color: "#bbaf05", // yellow when selected
                            },
                          }}
                        />
                      }
                      label="Male"
                    />
                    <FormControlLabel
                      value="Female"
                      control={
                        <Radio
                          sx={{
                            color: "#8f8d8dff",
                            "&.Mui-checked": {
                              color: "#bbaf05", // yellow when selected
                            },
                          }}
                        />
                      }
                      label="Female"
                    />
                  </RadioGroup>
                </FormControl>
              </Grid>

            </Grid>
          </div>
          <div className="pagination-footer">
            <div></div>
            {isViewMode ? (
              <Button
                className="primary-btn"
                variant="contained"
                size="small"
                onClick={handleUpdateAdmin}
                startIcon={<PlusOutlined style={{ fontSize: "14px", color: "black" }} />}
                disabled={!name || !email || !contactNumber || !nic || !address || !gender}
              >
                Update Admin
              </Button>
            ) : (
              <Button
                className="primary-btn"
                variant="contained"
                size="small"
                onClick={handleAddAdmin}
                startIcon={<PlusOutlined style={{ fontSize: "14px", color: "black" }} />}
                disabled={!name || !email || !contactNumber || !password || !nic || !address || !gender}
              >
                Add Admin
              </Button>
            )}

          </div>
        </div>

      )}
    </div>
  );
}
